/* eslint-disable */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, DoCheck, ElementRef, Inject, NgZone, Type, ViewChild } from '@angular/core';
import { ModalController, NavParams, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, skipWhile } from 'rxjs/operators';
import { AddressSearchResult } from 'src/app/components/address-search/address-search.component';
import { GoogleMapsController } from 'src/app/components/map/google-map/controllers/google-maps.controller';
import { GoogleMapsComponent } from 'src/app/components/map/google-map/google-maps.component';
import { GoogleMapsState } from 'src/app/components/map/google-map/google-maps.state';
import { MarkerConfig } from 'src/app/components/map/google-map/marker/marker.config';
import { CameraData } from 'src/app/components/map/google-map/models/camera';
import { MSLatLng, MSLatLngPlace, MSLatLonPlace } from 'src/app/models/map.model';
import { CONSTANTS } from 'src/app/services/config.service';
import { GeolocationPermission } from 'src/app/services/location/geolocation-permission';
import { GeolocationService } from 'src/app/services/location/geolocation.service';
import { LoggerService } from 'src/app/services/logger.service';
import { MapsService } from 'src/app/services/maps.service';
import { PointsHelperService } from 'src/app/services/points-helper.service';
import { ReverseGeocodeUseCase } from 'src/app/use-case/bing-maps/reverse-geocode/reverse-geocode.use-case';
import { ReverseGeocodeEvent } from 'src/app/use-case/bing-maps/reverse-geocode/utils/reverse-geocode.utils';
import { identifyPlatform, PlatformEnum } from 'src/app/utils/platform.utils';
import { AssetWrapper } from 'src/app/wrappers/asset.wrapper';
import { SurveyGeocoderBrowserPage } from '../browser/survey-geocoder-browser.page';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';

@Component({
  selector: 'app-survey-geocoder',
  templateUrl: './survey-geocoder.page.html',
  styleUrls: ['./survey-geocoder.page.scss']
})
export class SurveyGeocoderPage implements DoCheck {
  @ViewChild('placesMap') placesMap: ElementRef;
  @ViewChild(GoogleMapsComponent) map: GoogleMapsComponent;

  onMapSubscriptions: Subscription[] = [];

  data: any = {};
  survey: any = {};
  center: any;

  query = '';
  // searchPlaceholder: string;
  searchResults: any = [];
  hasValidAccuracy = true;
  invalidMapSearch = false;
  zoomedInEnough = false;

  nextDisabled = true;
  mapLoaded = false;
  placesLoaded = false;
  isDevice: boolean = CONSTANTS.IS_DEVICE;

  places: any = [];
  location: MSLatLngPlace;

  mapIsHidden = false;
  showAddressDialog = true;
  addressDialogText: string = this.getAddressDialogText();

  isInited = new BehaviorSubject<boolean>(false);

  constructor(
    private navParams: NavParams,
    private zone: NgZone,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private logger: LoggerService,
    private pointsHelper: PointsHelperService,
    private maps: MapsService,
    private platform: Platform,
    private changeDetectorRef: ChangeDetectorRef,
    private geolocationService: GeolocationService,
    private assetWrapper: AssetWrapper,
    private reverseGeocoder: ReverseGeocodeUseCase,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.platform.keyboardDidShow.subscribe(() => {
      this.showAddressDialog = false;
      this.changeDetectorRef.detectChanges();
    });
    this.platform.keyboardDidHide.subscribe(() => {
      this.showAddressDialog = true;
      this.changeDetectorRef.detectChanges();
    });

    this.isInited.subscribe((inited) => {
      this.mapLoaded = inited;
    });
  }

  /************************************************************************************************************************************************************************
   * HELP
   ***********************************************************************************************************************************************************************/

  async onClickHelp() {
    this.maps.presentAlertGeocoderInstructions();
  }

  /************************************************************************************************************************************************************************
   * MANIPULATE MAP
   ***********************************************************************************************************************************************************************/

  async updateMarker(location: MSLatLngPlace) {
    await this.maps.setMarkerPositionAsync(this.maps.markers[0], {
      lat: location.lat,
      lng: location.lng
    });
    await this.maps.setMarkerVisibleAsync(this.maps.markers[0], true);
  }

  /************************************************************************************************************************************************************************
   * ADDRESS SEARCH
   ***********************************************************************************************************************************************************************/

  onAddressSearchEvent($event: AddressSearchResult) {
    this.logger.log($event, MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE);
    if ($event.type == 'select') {
      this.onSelectAddress($event);
    }
  }

  onAddressSearchListEvent($event: AddressSearchResult[]) {
    //Google maps does not display when completely covered by list results. Reload to display map.
    if ($event.length == 0) {
      this.initiateMap();
    }
  }

  async onSelectAddress(res: AddressSearchResult) {
    this.hasValidAccuracy = res.hasValidAccuracy;
    this.invalidMapSearch = false;

    const location = res.hasValidAccuracy ? res.location : null;

    this.isInited
      .asObservable()
      .pipe(first((inited) => inited == true))
      .subscribe(async () => {
        await this.handleSetLocation(location);
        await this.maps.setMapCenter(location);
      });
  }

  /************************************************************************************************************************************************************************
   * MAP SEARCH
   ***********************************************************************************************************************************************************************/

  onMapClicked(latLng: MSLatLng) {
    this.zone.run(async () => {
      if (!this.zoomedInEnough) {
        return;
      }
      const response = await this.reverseGeocoder
        .execute(latLng)
        .pipe(
          skipWhile((response) => response instanceof ReverseGeocodeEvent.Loading),
          first()
        )
        .toPromise();
      const location = response instanceof ReverseGeocodeEvent.Success ? response.place : null;

      this.invalidMapSearch = location == null;
      this.hasValidAccuracy = true;

      await this.handleSetLocation(location);
    });
  }

  async reverseGeocode(latLng: MSLatLng): Promise<MSLatLngPlace | null> {
    const res = await this.maps
      .reverseGeocode(latLng)
      .then((res: any) => {
        //Use previous latlng, the reverse geocoder moves the coordinates slightly
        const location: MSLatLngPlace = {
          lat: latLng.lat,
          lng: latLng.lng,
          name: res.location.name
        };
        return location;
      })
      .catch<null>((err) => null);
    return res;
  }

  /************************************************************************************************************************************************************************
   * PREPARE DATA FOR STORING
   ***********************************************************************************************************************************************************************/

  getLatLonPlace(): MSLatLonPlace | null {
    if (!this.location) {
      return null;
    }
    const intLoc = this.pointsHelper.getLatLonFromLatLng(this.location);
    const latLonPlace = {
      lat: intLoc.lat,
      lon: intLoc.lon,
      name: this.location.name
    };

    return latLonPlace;
  }

  next() {
    const data = this.getLatLonPlace();
    this.modalCtrl.dismiss(data);
  }

  async dismiss() {
    this.modalCtrl.dismiss();
  }

  async init(controller: GoogleMapsController) {
    this.logger.log('ionViewDidLoad SurveyGeocoderPage', MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE);
    this.logger.log('cur lang', MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE, this.translate.currentLang);
    // this.translate.use(this.translate.currentLang);

    // get searchbar element and disable until map is loaded
    const inputs: any = this.document.getElementById('searchbar-input').getElementsByTagName('INPUT');
    inputs[0].disabled = true;

    // navParams passes the question, the item (including a JSON parsed location object), and surveyData (so we can use homeLoc for centering the map)
    this.data = this.navParams.data;
    // get the place obj
    const place = this.data.item.place;
    let point: MSLatLng | undefined;

    if (this.location) {
      point = this.location;
      this.center = point;
    } else if (place) {
      // if we already have a place, center the map there
      point = this.pointsHelper.getLatLngFromLatLon({
        lat: place.lat,
        lon: place.lon
      });
      this.center = point;
      try {
        this.location = await this.reverseGeocode(point);
      } catch (err) {
        this.location = null;
      }
      // if we don't have a point, center on the homeLoc if we have it
    } else if (this.data.surveyData.hhHome) {
      const obj = JSON.parse(this.data.surveyData.hhHome);
      this.center = this.pointsHelper.getLatLngFromLatLon({
        lat: obj.lat,
        lon: obj.lon
      });
    }

    return this.maps
      .init(controller, this.center, null)
      .then(() => {
        this.logger.log('this.center: ', MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE, this.center);
        this.logger.log(`CONSTANTS.MAPS.DEFAULT_MAP_CENTER: ${JSON.stringify(CONSTANTS.MAPS.DEFAULT_MAP_CENTER)}`, MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE);

        const markerConfig: MarkerConfig = {
          coordinate: this.center ? this.center : CONSTANTS.MAPS.DEFAULT_MAP_CENTER,
          visible: point != null,
          iconUrl: this.assetWrapper.markerGeneric
        };

        // zoom out from home loc if work/school so users less likely to choose home loc as work or school loc
        if (this.data.question && this.data.question.type === 'geocoders') {
          // if we have place - i.e., the user has answered and re-entered this page, use the map service default zoom of 16
          // however, if we have a home loc as our center, zoom out a bit
          if (!place && this.center) {
            this.maps.setMapZoom(13);
          }
        }

        return this.maps.addMarker(markerConfig);
      })
      .then(() => this.maps.loadPlacesService(this.placesMap.nativeElement, this.center, null))
      .then((res) => {
        if (res) {
          inputs[0].disabled = false;
        }

        if (point) {
          this.nextDisabled = false;
        }

        this.isInited.next(true);
      })
      .catch((err) => {
        this.logger.log('survey-geocoder error: ', MSRemoteLoggerCategory.SURVEY_GEOCODER_DEVICE_PAGE, err);
      });
  }

  //This should handle all of the logic for setting and validating the location, as well as handling what to do next
  private async handleSetLocation(location: MSLatLngPlace | null) {
    if (location && location.name && this.hasValidAccuracy && !this.invalidMapSearch) {
      this.nextDisabled = false;
      this.location = location;

      await this.updateMarker(location);
      return;
    }

    this.nextDisabled = true;
    this.location = null;
  }

  async mapStateUpdate(state: GoogleMapsState.Spec) {
    if (state instanceof GoogleMapsState.Ready) {
      const controller = state.controller;

      await this.init(controller);
      const onClickSubscription = controller.onMapClickSignal.subscribe((latLng: MSLatLng) => {
        this.onMapClicked(latLng);
      });
      const onCameraSubscription = controller.cameraState.subscribe((cameraData: CameraData) => {
        this.zone.run(() => {
          this.zoomedInEnough = cameraData.zoom >= CONSTANTS.MAPS.PLACE_MAP_ZOOM;
        });
        this.changeDetectorRef.detectChanges();
      });

      this.onMapSubscriptions.push(onClickSubscription);
      this.onMapSubscriptions.push(onCameraSubscription);
    }
    if (state instanceof GoogleMapsState.Shutdown) {
      this.clearMapSubscriptions();
    }
  }

  async ionViewDidEnter() {
    this.map.setUp();
    this.initiateMap();
  }

  async ionViewWillLeave() {
    await this.maps.removeMap(true);
    await this.map.tearDown();
  }

  ngDoCheck() {
    this.addressDialogText = this.getAddressDialogText();
  }

  getAddressDialogText(): string {
    if (!this.zoomedInEnough) {
      return this.translate.instant('MAP_MAP_SEARCH_ZOOM');
    }
    if (this.location?.name) {
      return this.location.name;
    } else if (this.invalidMapSearch) {
      return this.translate.instant('MAP_MAP_SEARCH_NO_LOC');
    } else if (!this.hasValidAccuracy) {
      return this.translate.instant('MAP_PLACE_SEARCH_INACCURATE');
    } else {
      return this.translate.instant('MAP_MAP_NO_ADDRESS');
    }
  }

  private async initiateMap() {
    this.isInited.next(false);

    let permission = await this.geolocationService.requestPermission();
    if (permission == GeolocationPermission.MSPermissionStatus.PROMPT_WITH_RATIONAL) {
      permission = await this.geolocationService.requestPermission();
    }

    const enableCurrentLocation = permission == GeolocationPermission.MSPermissionStatus.GRANTED;

    await this.maps.removeMap(true);
    this.clearMapSubscriptions();

    await this.map.initiateMap(enableCurrentLocation);
  }

  private clearMapSubscriptions() {
    this.onMapSubscriptions.forEach((onMapSubscription) => {
      onMapSubscription.unsubscribe();
    });
    this.onMapSubscriptions = [];
  }

  static selectSurveyMapPage(platform: Platform): Type<SurveyGeocoderPage> | Type<SurveyGeocoderBrowserPage> {
    const isBrowserMove = identifyPlatform(platform) === PlatformEnum.BROWSER;
    return isBrowserMove ? SurveyGeocoderBrowserPage : SurveyGeocoderPage;
  }
}
